import { useState } from 'react'
import { ChevronDown, LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useSidebar } from './sidebar'

interface CollapsibleSidebarGroupProps {
  label: string
  icon?: LucideIcon
  children: React.ReactNode
  defaultOpen?: boolean
  className?: string
}

export function CollapsibleSidebarGroup({
  label,
  icon: Icon,
  children,
  defaultOpen = false,
  className
}: CollapsibleSidebarGroupProps) {
  const { collapsed, setCollapsed } = useSidebar()
  const [isOpen, setIsOpen] = useState(defaultOpen)

  // Collapsed: render only the group icon. Click expands the sidebar and opens the group.
  if (collapsed) {
    return (
      <div className={cn("mb-1 px-1", className)}>
        <button
          onClick={() => {
            setCollapsed(false)
            setIsOpen(true)
          }}
          className={cn(
            "flex w-full items-center justify-center rounded-md px-2 py-1.5 transition-all",
            "text-muted-foreground hover:bg-accent/20 hover:text-foreground"
          )}
          title={label}
          aria-label={label}
        >
          {Icon && <Icon className="h-4 w-4" />}
        </button>
      </div>
    )
  }

  return (
    <div className={cn("mb-4", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between w-full px-2 py-1.5 text-13 font-normal transition-all rounded-md",
          "text-muted-foreground hover:bg-accent/20 group"
        )}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" />}
          <span>{label}</span>
        </div>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200 opacity-60 group-hover:opacity-100",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div className="mt-1 ml-4 pl-3 space-y-0.5 border-l border-border">
          {children}
        </div>
      )}
    </div>
  )
}
